import React, {useState, useEffect, useCallback} from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'

import {createKeyManager} from 'utils/encryption.utils'
import DefaultLayout from 'modules/common/components/Layout'
import ChatLoginForm from 'modules/chat/components/ChatLoginForm'
import ChatBox from 'modules/chat/components/ChatBox'
import useNavigationPrompt from 'hooks/useNavigationPrompt'

const ChatRoom = ({room, showDetailsOnLoad}) => {
  const [isCreatingKeyManager, setIsCreatingKeyManager] = useState(false)
  const [keyManager, setKeyManager] = useState(null)
  const [isPinInValid, setIsPinInValid] = useState(false)
  const [userName, setUserName] = useState(null)
  const [pin, setPin] = useState(null)

  const roomId = get(room, 'uid')
  const encryptionKeys = get(room, 'keys')
  const description = get(room, 'description')
  const creatorName = get(room, 'creator.name')
  const isLoggedIn = userName && pin && keyManager

  useNavigationPrompt(
    isLoggedIn,
    'Are you sure you want to navigate away from the page?'
  )

  useEffect(() => {
    if (!roomId) return

    if (process.browser) {
      const storageUtils = require('modules/chat/utils/storage.utils')
      setUserName(storageUtils.getName(roomId))
      setPin(storageUtils.getPin(roomId))
    }
  }, [roomId])

  useEffect(() => {
    if (!pin || !encryptionKeys) return

    async function asyncSetKeyManager() {
      setIsCreatingKeyManager(true)
      let keyManager = null
      try {
        keyManager = await createKeyManager(encryptionKeys, pin)
        setIsPinInValid(false)
      } catch (err) {
        setIsPinInValid(true)
      }
      // if key manager was successfully created
      // save name and pin in local storage
      if (process.browser) {
        if (keyManager) {
          const storageUtils = require('modules/chat/utils/storage.utils')
          storageUtils.setName(roomId, userName)
          storageUtils.setPin(roomId, pin)
        }
      }
      setKeyManager(keyManager)
      setIsCreatingKeyManager(false)
    }

    asyncSetKeyManager()
  }, [userName, pin, roomId, encryptionKeys])

  const handleChatLogin = useCallback((values, {setSubmitting}) => {
    setUserName(values.name)
    setPin(values.pin)
    setSubmitting(false)
  }, [])

  let heroBody = null
  let heroContainerClassName = null

  if (!isLoggedIn) {
    heroContainerClassName = 'is-fullheight'
    heroBody = (
      <div className="columns is-vcentered is-gapless is-centered">
        <div className="column is-half">
          <div className="p-1">
            <ChatLoginForm
              onSubmit={handleChatLogin}
              hasError={isPinInValid}
              isLoading={isCreatingKeyManager}
              heading={
                <>
                  <h1 className="is-size-4 is-size-5-mobile is-uppercase has-letter-spacing has-text-centered">
                    Login
                  </h1>
                  <p className="is-size-7 has-text-centered">
                    {creatorName ? `Chat with ${creatorName}` : null}{' '}
                    {description ? `about ${description}` : null}
                  </p>
                </>
              }
            />
          </div>
        </div>
      </div>
    )
  } else {
    heroBody = (
      <div className="pt-1">
        <div className="columns is-centered is-vcentered is-gapless">
          <div className="column is-three-quarters">
            <ChatBox
              room={room}
              pin={pin}
              userName={userName}
              encryptionKeys={encryptionKeys}
              keyManager={keyManager}
              showDetailsOnLoad={showDetailsOnLoad}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <DefaultLayout
      heroContainerClassName={heroContainerClassName}
      heroBodyClassName="p-0-mobile pt-0 has-default-background-image"
      hero={heroBody}
    />
  )
}

ChatRoom.propTypes = {
  room: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    keys: PropTypes.shape({
      private: PropTypes.string.isRequired,
      public: PropTypes.string.isRequired,
    }),
  }),
  showDetailsOnLoad: PropTypes.bool,
}

export default ChatRoom
